/**
 * latexNormalize — shared LaTeX detection + plain-text conversion for
 * model-emitted math markup that reaches user-facing surfaces.
 *
 * Detection contract: a single-$ span counts as LaTeX ONLY if (a) it contains
 * a known backslash command — a real dollar amount can never contain one —
 * AND (b) NEITHER delimiter could be a currency symbol. Gate (b) is SYMMETRIC
 * and both halves are load-bearing:
 *   opener  `$(?!\s*[\d.,])`      — money opens "$5", "$.07", "$ 5,000", "$170,000"
 *   closer  `(?<!\s)$(?!\d|\.\d)`  — see below; this half is STRUCTURAL, not a list
 * The closer half was added 2026-08-31. Before it, gate (b) only stopped a span
 * STARTING on an amount, so widening the command set to include `\to`/`\rightarrow`
 * — the exact tokens a model puts between two figures — let a span open on a
 * non-digit-led amount and close on the following one, eating BOTH:
 *   "$.07/lead \to $.44"  →  ".07/lead →.44"      (money destroyed)
 * Amounts that do not lead with a digit are real and common here ($.07/lead,
 * $(12,000) accounting negatives, $-1,200, $ 5,000, $K, $M). Enumerating those
 * prefixes is a LOSING GAME — the first cut of the closer listed digits and
 * dots, and "$(12,000) \to $(4,000)" walked straight through it because BOTH
 * delimiters were non-digit-led. The closer is therefore structural, not a
 * list: a real TeX span never closes on whitespace ("$x \to y$"), while the
 * next amount's $ always follows one ("… \to $(4,000)"). `(?<!\s)` decides it
 * for every currency prefix at once, including ones nobody has thought of.
 * The `(?!\d|\.\d)` tail then keeps "$X$.07" from closing mid-amount while
 * still letting a span end a sentence ("the conversion is $A \to B$.").
 * Never loosen either half.
 *
 * A span may also be math for a reason OTHER than a command. Gate (a) asked
 * only "is there a backslash command in here?", so `$P_{max}$`, `$T$`, `$S$` —
 * the single-letter variables a model names a formula's terms with — answered
 * no and reached the reader as literal markup (Issue 98 residual, 2026-09-02).
 * A command-free span is therefore admitted when its interior is a bare math
 * IDENTIFIER (`MATH_IDENTIFIER_RE`). That shape is deliberately NARROW and is
 * the second money gate: the structural closer accepts a "/" before the next
 * `$`, so "$AAPL/$MSFT" and "$HOME/$USER" both reach the interior test with
 * `AAPL/` and `HOME/` in hand, and only the identifier shape refuses them.
 * Money cannot reach it at all — every currency form leads with a digit, a
 * dot, a comma, a sign, a paren or a space, none of which start an identifier.
 *
 * The recognised command set is DATA — `LATEX_SYMBOLS` below. Every regex and
 * every replacement in this file is derived from it, and the MessageBubble
 * inline splitter imports `DOLLAR_LATEX_SPAN_SOURCE` rather than restating the
 * pattern. Extend by adding a row to the table; nothing else needs editing.
 * (Before 2026-08-31 the list was hand-written in three places and covered
 * arithmetic only, so every arrow and Greek letter — `$\rightarrow$` most of
 * all — failed both gates and reached the reader as raw markup.)
 *
 * Fenced/inline code is never touched — LaTeX source inside code is content,
 * not markup (and shell/code text legitimately contains \$).
 *
 * KNOWN COLLISION SURFACE (accepted): the set includes short tokens — to, in,
 * int, ne, le, ge, pm, mu, nu, xi, pi, ll, gg, cap, cup, sim, sum, div, ell —
 * so backslash-bearing NON-LaTeX text outside code spans can match:
 * "C:\to" becomes "C:→". Backtick spans are shielded; bare Windows paths in
 * prose are not. Judged acceptable (rare here, and the alternative is losing
 * the arrows this exists to fix) — but weigh it before widening further.
 *
 * Consumers: MessageBubble splitter (single-$ render fallback), ResearchPanel /
 * LearnPanel (plain-text normalize), useElevenLabs (TTS pre-clean), and the
 * backend history guard. Mirror copy at backend/src/utils/latexNormalize.js —
 * `latexNormalize.mirror.test.js` fails the suite if the two drift.
 */

// command → Unicode. The single source of the recognised set.
const LATEX_SYMBOLS = {
  // arrows — the 2026-08-31 offender class
  rightarrow: '→',
  leftarrow: '←',
  leftrightarrow: '↔',
  Rightarrow: '⇒',
  Leftarrow: '⇐',
  Leftrightarrow: '⇔',
  longrightarrow: '⟶',
  longleftarrow: '⟵',
  mapsto: '↦',
  uparrow: '↑',
  downarrow: '↓',
  to: '→',
  gets: '←',
  // arithmetic / relational — the original 2026-07-16 set
  times: '×',
  div: '÷',
  approx: '≈',
  cdot: '·',
  pm: '±',
  mp: '∓',
  leq: '≤',
  geq: '≥',
  neq: '≠',
  le: '≤',
  ge: '≥',
  ne: '≠',
  equiv: '≡',
  sim: '∼',
  simeq: '≃',
  propto: '∝',
  ll: '≪',
  gg: '≫',
  // set / logic
  in: '∈',
  notin: '∉',
  subset: '⊂',
  subseteq: '⊆',
  supset: '⊃',
  supseteq: '⊇',
  cup: '∪',
  cap: '∩',
  forall: '∀',
  exists: '∃',
  emptyset: '∅',
  therefore: '∴',
  because: '∵',
  // analysis
  infty: '∞',
  ell: 'ℓ',
  hbar: 'ℏ',
  partial: '∂',
  nabla: '∇',
  sum: '∑',
  prod: '∏',
  int: '∫',
  // punctuation / misc
  ldots: '…',
  cdots: '⋯',
  dots: '…',
  degree: '°',
  circ: '∘',
  bullet: '•',
  ast: '∗',
  star: '⋆',
  angle: '∠',
  perp: '⊥',
  parallel: '∥',
  // greek, lowercase
  alpha: 'α', beta: 'β', gamma: 'γ', delta: 'δ', epsilon: 'ε',
  varepsilon: 'ε', zeta: 'ζ', eta: 'η', theta: 'θ', vartheta: 'ϑ',
  iota: 'ι', kappa: 'κ', lambda: 'λ', mu: 'μ', nu: 'ν', xi: 'ξ',
  pi: 'π', rho: 'ρ', sigma: 'σ', tau: 'τ', upsilon: 'υ', phi: 'ϕ',
  varphi: 'φ', chi: 'χ', psi: 'ψ', omega: 'ω',
  // greek, uppercase
  Gamma: 'Γ', Delta: 'Δ', Theta: 'Θ', Lambda: 'Λ', Xi: 'Ξ', Pi: 'Π',
  Sigma: 'Σ', Upsilon: 'Υ', Phi: 'Φ', Psi: 'Ψ', Omega: 'Ω',
};

// The distinct glyphs this module can emit. Exported so the TTS speech table
// can be parity-tested against it — a glyph with no spoken form is dropped
// silently mid-sentence, which is worse than the markup it replaced.
export const LATEX_GLYPHS = Object.freeze(
  [...new Set(Object.values(LATEX_SYMBOLS))].filter(Boolean).sort(),
);

// Commands that take braces — converted by replaceStructural, not by the
// symbol table, but still part of the recognised set for DETECTION.
// `left`/`right` are size modifiers: they are DELETED rather than substituted,
// so unlike every other row a false positive is invisible — it eats a word.
// They are therefore handled in replaceStructural behind a delimiter lookahead
// ("\left(" yes, "the \left side of the deal" no) and deliberately kept OUT of
// LATEX_SYMBOLS, whose contract is "every value is a visible glyph".
const LATEX_STRUCTURAL = ['frac', 'sqrt', 'mathbf', 'mathrm', 'mathit', 'text',
  'left', 'right'];

// Longest-first so `\infty` is never captured as `\in`, `\int` never as `\in`,
// and `\longrightarrow` never as `\to`. Ties broken alphabetically so the
// generated pattern is stable across runs (the mirror test compares bytes).
export const LATEX_COMMAND_ALTERNATION = [
  ...Object.keys(LATEX_SYMBOLS),
  ...LATEX_STRUCTURAL,
].sort((a, b) => b.length - a.length || a.localeCompare(b)).join('|');

const LATEX_COMMAND_RE = new RegExp(`\\\\(?:${LATEX_COMMAND_ALTERNATION})\\b`);

// The command-free half of the detection gate: one letter, optionally carrying
// sub/superscripts. Anchored, so it tests a WHOLE span interior and nothing
// less. Kept to a single leading letter on purpose — a multi-letter bare word
// ("$AAPL$", "$USER$") is far more likely a ticker or a shell variable than a
// variable name, and admitting it buys nothing the command path does not
// already cover. Two scripts is enough for `P_{max}^{2}`; more is not math a
// chat reply writes inline. A delimited `$T$` IS admitted as math — a bare `$T`
// ticker in prose has no closer and is never touched (review 2026-09-02).
const MATH_IDENTIFIER_RE = /^[A-Za-z](?:[_^](?:\{[A-Za-z0-9]{1,16}\}|[A-Za-z0-9])){0,2}$/;

// Is this span interior LaTeX? The ONE place both halves of gate (a) live, so
// detection and the `containsLatexCommands` predicate can never disagree.
function isLatexSpanInterior(tex) {
  return LATEX_COMMAND_RE.test(tex) || MATH_IDENTIFIER_RE.test(tex);
}

// Single-$ span carrying a command, as a SOURCE string so the MessageBubble
// inline splitter composes with it instead of restating it. Mirrors
// DOLLAR_SPAN_RE's gates: (?!\d) money gate, no newline, \$ allowed inside.
export const DOLLAR_LATEX_SPAN_SOURCE =
  `\\$(?!\\s*[\\d.,])(?:\\\\\\$|[^$\\n])*?\\\\(?:${LATEX_COMMAND_ALTERNATION})\\b(?:\\\\\\$|[^$\\n])*(?<!\\s)\\$(?!\\d|\\.\\d)`;

// One single-$ span on a single line; escaped dollars (\$) allowed inside.
// BOTH lookarounds are the money gate — see the detection contract above.
const DOLLAR_SPAN_RE = /\$(?!\s*[\d.,])((?:\\\$|[^$\n])+)(?<!\s)\$(?!\d|\.\d)/g;

// Fenced code blocks and single-line inline code.
const CODE_SPAN_RE = /```[\s\S]*?```|`[^`\n]+`/g;

// Every recognised symbol in one pass — a map lookup, so no chain of
// replacements can shadow another and adding a row cannot reorder anything.
// Structural commands fall through unchanged (handled before this runs).
const SYMBOL_RE = new RegExp(`\\\\(${LATEX_COMMAND_ALTERNATION})\\b`, 'g');

// True when the text carries markup this module can convert: a bare backslash
// command anywhere, or a delimited command-free math span. The second half is
// what lets a caller that short-circuits on this predicate (the MessageBubble
// inline fallback) still see `$P_{max}$` — a false answer there is why the
// residual reached the reader with the normalizer sitting right next to it.
// STRICT half only — a bare backslash command anywhere. The MessageBubble
// KaTeX gate uses THIS, so the identifier admission below never widens what
// reaches dangerouslySetInnerHTML; identifier spans take the plain-text path.
export function containsLatexCommand(text) {
  return typeof text === 'string' && LATEX_COMMAND_RE.test(text);
}

export function containsLatexCommands(text) {
  if (typeof text !== 'string') return false;
  return LATEX_COMMAND_RE.test(text) || findDollarLatexSpans(text).length > 0;
}

/**
 * Find single-$ spans whose content is LaTeX — a command, or a bare math
 * identifier (see the detection contract).
 * Returns [{ start, end, tex }] — start/end index the delimiters inclusive.
 * Non-LaTeX spans are skipped and their closing $ stays available as a
 * potential opener ("$100 and $\approx$" still finds the second span).
 */
export function findDollarLatexSpans(text) {
  if (typeof text !== 'string' || text.indexOf('$') === -1) return [];
  const spans = [];
  DOLLAR_SPAN_RE.lastIndex = 0;
  let m;
  while ((m = DOLLAR_SPAN_RE.exec(text)) !== null) {
    if (isLatexSpanInterior(m[1])) {
      spans.push({ start: m.index, end: m.index + m[0].length, tex: m[1] });
    } else {
      // Not LaTeX — jump straight to the closing $, which stays available as
      // the next opener. Jumping (not index+1) avoids an O(n²) interior
      // rescan on pathological content.
      DOLLAR_SPAN_RE.lastIndex = m.index + m[0].length - 1;
    }
  }
  return spans;
}

// Brace-taking commands. Shared by both conversion passes.
function replaceStructural(text) {
  return text
    .replace(/\\mathbf\{([^{}]*)\}/g, '$1')
    .replace(/\\mathrm\{([^{}]*)\}/g, '$1')
    .replace(/\\mathit\{([^{}]*)\}/g, '$1')
    .replace(/\\text\{([^{}]*)\}/g, '$1')
    .replace(/\\frac\{([^{}]*)\}\{([^{}]*)\}/g, '$1/$2')
    .replace(/\\sqrt\{([^{}]*)\}/g, '√$1')
    // Size modifiers, deleted only where a real delimiter follows. `.` and `/`
    // are deliberately NOT delimiters here: "\right." and "\left/right split"
    // are prose, and treating them as TeX ate the sentence and the word. The
    // null-delimiter form is handled in convertMathSpan, where the text is
    // known to be math and that reading is the correct one.
    .replace(/\\(?:left|right)(?=[()[\]{}|\\]|\s*[()[\]{}|])/g, '');
}

// Symbol table lookup. Anything matching the command shape but absent from the
// table (i.e. the structural names) is left exactly as found.
function replaceSymbols(text) {
  return text.replace(SYMBOL_RE, (whole, name) =>
    Object.prototype.hasOwnProperty.call(LATEX_SYMBOLS, name) ? LATEX_SYMBOLS[name] : whole);
}

// Full conversion for text KNOWN to be LaTeX (delimited span interiors only):
// brace + symbol commands, escaped \$ / \%, spacing commands, whitespace tidy.
function convertMathSpan(tex) {
  // TeX's null delimiter (`\right.`). MATH-ONLY by design: in arbitrary prose
  // "\right." is a word followed by a full stop, and deleting it eats the
  // sentence — which is exactly what a shared rule did (review, 2026-08-31).
  const math = tex.replace(/\\(?:left|right)\s*\./g, '');
  return replaceSymbols(replaceStructural(math))
    // Sub/superscript braces: `P_{max}` -> `P_max`, `x^{2}` -> `x^2`. MATH-ONLY,
    // like the null delimiter above — in prose "a_{b}" is not a subscript.
    // ASCII, never Unicode subscripts (Pₘₐₓ): those would be NEW glyphs,
    // and every glyph this module can emit owes the TTS table a spoken form
    // (speechSymbols.parity.test.js). Unicode also has no subscript letter for
    // b/c/d/f/g/q/w/y/z, so half of all identifiers would degrade silently.
    .replace(/([_^])\{([^{}]*)\}/g, '$1$2')
    .replace(/\\([{}])/g, '$1')
    .replace(/\\%/g, '%')
    .replace(/\\\$/g, '$')
    .replace(/\\[,;!]/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

// Conservative conversion for arbitrary prose (the final whole-text pass):
// ONLY the unambiguous command tokens — no \$ / \% unescapes, no whitespace
// rewrites — so non-LaTeX regions (prose spacing, code-adjacent text) are
// never altered by a stray command elsewhere in the message.
function convertBareCommands(text) {
  return replaceSymbols(replaceStructural(text));
}

// Apply fn to every non-code segment; code spans pass through untouched.
function convertOutsideCode(text, fn) {
  if (text.indexOf('`') === -1) return fn(text);
  let out = '';
  let cursor = 0;
  CODE_SPAN_RE.lastIndex = 0;
  let m;
  while ((m = CODE_SPAN_RE.exec(text)) !== null) {
    out += fn(text.slice(cursor, m.index)) + m[0];
    cursor = m.index + m[0].length;
  }
  return out + fn(text.slice(cursor));
}

/**
 * Convert model-emitted LaTeX to plain readable text: $$…$$ / \[…\] blocks,
 * \(…\) inline, detection-gated single-$ spans, then bare commands in prose.
 * Code spans are never modified. Idempotent; no-op on text with no LaTeX.
 * Dollar amounts pass through byte-identical (see detection contract above).
 */
export function latexToPlainText(text) {
  // `$` as well as `\` — a command-free span ("$T$") carries no backslash, and
  // skipping on that alone is what made this function a no-op on the whole
  // Issue 98 residual. Text with neither still costs one indexOf.
  if (typeof text !== 'string' || text.length === 0
      || (text.indexOf('\\') === -1 && text.indexOf('$') === -1)) {
    return text;
  }
  return convertOutsideCode(text, (seg) => {
    let out = seg;

    // Display math only when the interior looks like math (a backslash, a
    // script or an `=`) and carries no `$` of its own. The relaxed early return
    // above lets backslash-free text reach this line, so "$$$ pricing" and
    // "$$5 to $$10" must pass through byte-identical (review 2026-09-02).
    out = out.replace(/\$\$([\s\S]+?)\$\$/g, (m, tex) =>
      (tex.includes('$') || !/[\\_^=]/.test(tex)) ? m : convertMathSpan(tex));
    out = out.replace(/\\\[([\s\S]+?)\\\]/g, (_, tex) => convertMathSpan(tex));
    out = out.replace(/\\\(([\s\S]+?)\\\)/g, (_, tex) => convertMathSpan(tex));

    const spans = findDollarLatexSpans(out);
    if (spans.length > 0) {
      let rebuilt = '';
      let cursor = 0;
      for (const s of spans) {
        rebuilt += out.slice(cursor, s.start) + convertMathSpan(s.tex);
        cursor = s.end;
      }
      out = rebuilt + out.slice(cursor);
    }

    if (LATEX_COMMAND_RE.test(out)) out = convertBareCommands(out);

    return out;
  });
}
